import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion";
import { Container } from "@/components/Section";
import { AccordionItem } from "@/components/Accordion";
import { FAQS, FAQ_DISCLAIMER } from "@/data/content";

const CATS = ["Revenue & Fees", "Your Property", "Day-to-Day", "Suitability"];

export default function Faqs() {
  return (
    <main data-testid="faqs-page">
      <section className="noise-dark bg-forest pb-24 pt-44 text-cream sm:pb-32 sm:pt-52">
        <Container className="relative z-10">
          <FadeUp>
            <p className="eyebrow text-sage">FAQs</p>
          </FadeUp>
          <MaskedLines
            className="mt-6 max-w-4xl"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
            lines={[
              <span key="1">Everything owners</span>,
              <span key="2" className="italic text-sage-light">usually ask us.</span>,
            ]}
          />
          <FadeUp delay={0.35}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/70">
              Straight answers on revenue, regulations, guests and the day-to-day. Anything else — just ask.
            </p>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-1">
              {CATS.map((c, i) => (
                <a
                  key={c}
                  href={`#cat-${i}`}
                  data-testid={`faq-category-link-${i}`}
                  className="block rounded-lg px-4 py-3 text-sm font-semibold text-charcoal/60 transition-colors duration-300 hover:bg-bone hover:text-forest"
                >
                  {c}
                </a>
              ))}
            </div>
          </div>
          <div className="lg:col-span-9">
            {CATS.map((c, ci) => (
              <div key={c} id={`cat-${ci}`} className="mb-14 scroll-mt-28">
                <FadeUp>
                  <p className="eyebrow text-forest-700">{c}</p>
                  <div className="mt-4 border-t border-forest/10">
                    {FAQS.filter((f) => f.cat === c).map((f, i) => (
                      <AccordionItem key={f.q} q={f.q} a={f.a} testid={`faq-accordion-item-${ci}-${i}`} />
                    ))}
                  </div>
                </FadeUp>
              </div>
            ))}
            <FadeUp>
              <div className="rounded-2xl border border-forest/12 bg-bone p-8" data-testid="faq-disclaimer">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal/45">A note on regulations</p>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{FAQ_DISCLAIMER}</p>
              </div>
            </FadeUp>
          </div>
        </Container>
      </section>

      <section className="noise-dark bg-forest py-24 text-cream sm:py-28">
        <Container className="relative z-10 flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <FadeUp>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium">Still wondering about your property?</h2>
              <p className="mt-5 text-base leading-relaxed text-cream/65">
                The fastest way to an answer is a property assessment — realistic figures within one working day.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/assessment"
              data-testid="faqs-bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
            >
              Get Your Property Assessment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
